import { useEffect, useMemo, useRef, useState } from "react";
import {
  cloneDefaultFilters,
  cloneDefaultLayout,
  DEFAULT_PREFERENCES,
  RESULT_COLUMNS,
} from "../constants/defaults";
import useScenarios from "./useScenarios";
import useSavedViews from "./useSavedViews";
import mockPlan from "../mocks/mockPlan";
import mockRealized from "../mocks/mockRealized";
import useSimulationBases from "./useSimulationBases";
import { scenarioRepositoryMock } from "../services/scenarioStorage.mock";
import simulationEngineMock from "../services/simulationEngine.mock";
import { aggregateRows } from "../utils/simulationCalc";
import { buildResultRows, buildUniqueOptions, filterSimulationRows } from "../utils/mappers";

function buildHistoryFromRun(scenario, runSnapshot) {
  return {
    id: `run-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    timestamp: runSnapshot?.ranAt || new Date().toISOString(),
    scenarioId: scenario?.id,
    scenarioName: scenario?.name,
    period: scenario?.period,
    status: runSnapshot?.aggregate?.status || "-",
    attendancePct: runSnapshot?.aggregate?.attendancePct || 0,
    gap: runSnapshot?.aggregate?.gap || 0,
  };
}

function cloneViewConfig(config) {
  return {
    filters: { ...(config?.filters || {}) },
    groupBy: config?.groupBy || "machine",
    visibleColumns: Array.isArray(config?.visibleColumns)
      ? [...config.visibleColumns]
      : cloneDefaultLayout().visibleColumns,
  };
}

function buildSeedPlanForMachine(machine, periods) {
  const lineBase = {
    Corte: 1120,
    Prensagem: 1480,
    Usinagem: 1310,
    Montagem: 1210,
    Linha: 1000,
  };
  const riskLoadFactor = {
    Baixo: 0.95,
    Medio: 1,
    "Médio": 1,
    Alto: 1.08,
  };
  const periodFactors = {
    "2026-W09": 1,
    "2026-W10": 1.06,
    "2026-W11": 0.97,
  };

  return (periods || []).map((period, idx) => {
    const seed =
      (lineBase[machine.line] || 1000) *
      (riskLoadFactor[machine.riskLevel] || 1) *
      (periodFactors[period] || 1) *
      (0.92 + (idx % 4) * 0.03);
    return {
      id: `plan_${period}_${machine.id}`,
      period,
      machineId: machine.id,
      planUnits: Math.round(seed),
    };
  });
}

function buildSeedRealizedForMachine(planRow, riskLevel) {
  const factorByRisk = {
    Baixo: 0.98,
    Medio: 0.92,
    "Médio": 0.92,
    Alto: 0.86,
  };
  const factor = factorByRisk[riskLevel] || 0.9;
  return {
    id: `real_${planRow.period}_${planRow.machineId}`,
    period: planRow.period,
    machineId: planRow.machineId,
    realizedUnits: Math.round((Number(planRow.planUnits) || 0) * factor),
  };
}

function parseDate(dateStr) {
  const d = new Date(`${dateStr}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDays(date, amount) {
  const d = new Date(date);
  d.setDate(d.getDate() + amount);
  return d;
}

function formatIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoWeekKeyFromDate(dateStr, toIsoWeekKey) {
  return typeof toIsoWeekKey === "function" ? toIsoWeekKey(dateStr) : "";
}

function expandDateRange(startDate, endDate) {
  const start = parseDate(startDate);
  const end = parseDate(endDate || startDate);
  if (!start || !end) return [];
  const safeEnd = end >= start ? end : start;
  const out = [];
  for (let d = new Date(start); d <= safeEnd; d = addDays(d, 1)) {
    out.push(formatIsoDate(d));
  }
  return out;
}

function monthKeyFromIso(dateStr) {
  return String(dateStr || "").slice(0, 7);
}

function buildMonthDateList(monthKey) {
  const [yearRaw, monthRaw] = String(monthKey || "").split("-");
  const year = Number(yearRaw);
  const month = Number(monthRaw);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return [];
  const days = new Date(year, month, 0).getDate();
  return Array.from({ length: days }, (_, idx) => `${year}-${String(month).padStart(2, "0")}-${String(idx + 1).padStart(2, "0")}`);
}

export default function useSimulationWorkbench() {
  const scenariosApi = useScenarios();
  const viewsApi = useSavedViews();
  const basesApi = useSimulationBases();

  const [filters, setFilters] = useState(() => cloneDefaultFilters());
  const [layout, setLayout] = useState(() => cloneDefaultLayout());
  const [executionHistory, setExecutionHistory] = useState(() => {
    const storage = scenarioRepositoryMock.loadState();
    return Array.isArray(storage?.executionHistory) ? storage.executionHistory : [];
  });
  const [preferences, setPreferences] = useState(() => {
    const storage = scenarioRepositoryMock.loadState();
    return {
      ...DEFAULT_PREFERENCES,
      ...(storage?.preferences || {}),
    };
  });

  const viewInitializedRef = useRef(false);
  const seededRunsRef = useRef(false);
  const basesFingerprintRef = useRef("");

  const {
    scenarios,
    activeScenario,
    activeScenarioId,
    setActiveScenarioId,
    createScenario,
    duplicateScenario,
    renameScenario,
    deleteScenario,
    setScenarioKind,
    updateScenarioParameters,
    updateScenarioPeriod,
    saveScenarioRun,
    markAllDirty,
  } = scenariosApi;

  const {
    views,
    activeView,
    activeViewId,
    setActiveViewId,
    createView,
    updateView,
    deleteView,
  } = viewsApi;
  const {
    machines,
    teams,
    maintenanceWindows,
    contractHoursCalendar,
    teamAbsences,
    teamBoosts,
    operatorsSummary,
    calendarMaps,
    helpers: baseHelpers,
    actions: baseActions,
  } = basesApi;

  const periods = useMemo(
    () => [...new Set(mockPlan.map((row) => row.period))].sort(),
    []
  );

  const effectivePlanRows = useMemo(() => {
    const base = Array.isArray(mockPlan) ? [...mockPlan] : [];
    const known = new Set(base.map((row) => `${row.period}_${row.machineId}`));
    (machines || []).forEach((machine) => {
      buildSeedPlanForMachine(machine, periods).forEach((row) => {
        const key = `${row.period}_${row.machineId}`;
        if (!known.has(key)) {
          known.add(key);
          base.push(row);
        }
      });
    });
    return base;
  }, [machines, periods]);

  const effectiveRealizedRows = useMemo(() => {
    const base = Array.isArray(mockRealized) ? [...mockRealized] : [];
    const known = new Set(base.map((row) => `${row.period}_${row.machineId}`));
    const machineMap = new Map((machines || []).map((m) => [m.id, m]));
    effectivePlanRows.forEach((planRow) => {
      const key = `${planRow.period}_${planRow.machineId}`;
      if (known.has(key)) return;
      const machine = machineMap.get(planRow.machineId);
      base.push(buildSeedRealizedForMachine(planRow, machine?.riskLevel));
      known.add(key);
    });
    return base;
  }, [effectivePlanRows, machines]);

  const periodContext = useMemo(() => {
    const toIsoWeekKey = baseHelpers?.toIsoWeekKey;
    const contractHoursByMachinePeriod = {};
    const contractHoursByMachineDate = {};
    const maintenanceHoursByMachine = {};
    const maintenanceEntriesCountByPeriod = {};
    const recommendedOperatorsByPeriod = {};
    const machineOperatorFactorByPeriod = {};
    const contractDaysByMachinePeriod = {};

    (contractHoursCalendar || []).forEach((entry) => {
      if (!entry.machineId) return;
      const days = expandDateRange(entry.startDate, entry.endDate);
      const hoursPerDay = Number(entry.hoursPerDay ?? entry.hours) || 0;
      days.forEach((date) => {
        const period = isoWeekKeyFromDate(date, toIsoWeekKey);
        if (!period) return;
        const periodMachineKey = `${period}::${entry.machineId}`;
        const dateMachineKey = `${date}::${entry.machineId}`;
        contractHoursByMachinePeriod[periodMachineKey] =
          (contractHoursByMachinePeriod[periodMachineKey] || 0) + hoursPerDay;
        contractHoursByMachineDate[dateMachineKey] =
          (contractHoursByMachineDate[dateMachineKey] || 0) + hoursPerDay;
        contractDaysByMachinePeriod[periodMachineKey] =
          (contractDaysByMachinePeriod[periodMachineKey] || 0) + 1;
      });
    });

    const slotDeltaByMachinePeriod = {};
    (teamAbsences || []).forEach((entry) => {
      const machineId = entry.machineId || "";
      if (!machineId) return;
      expandDateRange(entry.startDate, entry.endDate).forEach((date) => {
        const period = isoWeekKeyFromDate(date, toIsoWeekKey);
        if (!period) return;
        const key = `${period}::${machineId}`;
        slotDeltaByMachinePeriod[key] = (slotDeltaByMachinePeriod[key] || 0) - 1;
      });
    });
    (teamBoosts || []).forEach((entry) => {
      const machineId = entry.machineId || "";
      if (!machineId) return;
      expandDateRange(entry.startDate, entry.endDate).forEach((date) => {
        const period = isoWeekKeyFromDate(date, toIsoWeekKey);
        if (!period) return;
        const key = `${period}::${machineId}`;
        slotDeltaByMachinePeriod[key] = (slotDeltaByMachinePeriod[key] || 0) + 1;
      });
    });

    const periodsSet = new Set(periods);
    Object.keys(contractHoursByMachinePeriod).forEach((compoundKey) => {
      const [period] = compoundKey.split("::");
      if (period) periodsSet.add(period);
    });

    Array.from(periodsSet).forEach((period) => {
      (machines || []).forEach((machine) => {
        const key = `${period}::${machine.id}`;
        const days = Math.max(1, contractDaysByMachinePeriod[key] || 7);
        const delta = slotDeltaByMachinePeriod[key] || 0;
        machineOperatorFactorByPeriod[key] = Math.max(0, 1 + delta / days);
      });

      const baselineRecommended = Number(operatorsSummary?.recommendedOperators) || 1;
      recommendedOperatorsByPeriod[period] = baselineRecommended;
    });

    (maintenanceWindows || []).forEach((entry) => {
      if (!entry.machineId) return;
      const dates = expandDateRange(entry.startDate, entry.endDate);
      if (!dates.length) return;

      const periodsTouched = new Set();
      dates.forEach((date) => {
        const period = isoWeekKeyFromDate(date, toIsoWeekKey);
        if (!period) return;
        const machinePeriodKey = `${period}::${entry.machineId}`;
        const machineDateKey = `${date}::${entry.machineId}`;
        const fallbackDailyHours =
          (contractHoursByMachinePeriod[machinePeriodKey] || 0) /
          Math.max(1, contractDaysByMachinePeriod[machinePeriodKey] || 7);
        const dailyHours = Number(contractHoursByMachineDate[machineDateKey]) || fallbackDailyHours || 24;
        const key = `${period}::${entry.machineId}`;
        maintenanceHoursByMachine[key] = (maintenanceHoursByMachine[key] || 0) + (dailyHours || 0);
        periodsTouched.add(period);
      });

      periodsTouched.forEach((period) => {
        maintenanceEntriesCountByPeriod[period] = (maintenanceEntriesCountByPeriod[period] || 0) + 1;
      });
    });

    return {
      contractHoursByMachinePeriod,
      maintenanceHoursByMachine,
      maintenanceEntriesCountByPeriod,
      recommendedOperatorsByPeriod,
      machineOperatorFactorByPeriod,
    };
  }, [
    baseHelpers,
    contractHoursCalendar,
    maintenanceWindows,
    teamAbsences,
    teamBoosts,
    teams,
    operatorsSummary,
    periods,
  ]);

  useEffect(() => {
    scenarioRepositoryMock.saveState({ executionHistory, preferences });
  }, [executionHistory, preferences]);

  useEffect(() => {
    if (viewInitializedRef.current) return;
    if (!activeView?.config) return;
    const cfg = cloneViewConfig(activeView.config);
    setFilters((prev) => ({ ...prev, ...cfg.filters }));
    setLayout({
      groupBy: cfg.groupBy,
      visibleColumns: cfg.visibleColumns,
    });
    viewInitializedRef.current = true;
  }, [activeView]);

  useEffect(() => {
    if (seededRunsRef.current) return;
    if (!scenarios.length) return;
    const missing = scenarios.filter((scenario) => !scenario.lastRun);
    if (!missing.length) {
      seededRunsRef.current = true;
      return;
    }

    missing.forEach((scenario) => {
      const runSnapshot = simulationEngineMock.runScenario({
        scenario,
        machines,
        planRows: effectivePlanRows,
        realizedRows: effectiveRealizedRows,
        operatorsSummary,
        maintenanceSchedule: maintenanceWindows,
        periodContext,
      });
      saveScenarioRun(scenario.id, runSnapshot);
    });
    seededRunsRef.current = true;
  }, [
    scenarios,
    saveScenarioRun,
    machines,
    effectivePlanRows,
    effectiveRealizedRows,
    operatorsSummary,
    maintenanceWindows,
    periodContext,
  ]);

  useEffect(() => {
    if (!seededRunsRef.current) return;
    const fingerprint = JSON.stringify({
      machines,
      teams,
      maintenanceWindows,
      contractHoursCalendar,
      teamAbsences,
      teamBoosts,
    });
    if (!basesFingerprintRef.current) {
      basesFingerprintRef.current = fingerprint;
      return;
    }
    if (basesFingerprintRef.current === fingerprint) return;
    basesFingerprintRef.current = fingerprint;
    markAllDirty();
  }, [
    machines,
    teams,
    maintenanceWindows,
    contractHoursCalendar,
    teamAbsences,
    teamBoosts,
    markAllDirty,
  ]);

  useEffect(() => {
    if (!preferences.compareScenarioId) {
      const fallback = scenarios.find((scenario) => scenario.id !== activeScenarioId)?.id || null;
      if (fallback) {
        setPreferences((prev) => ({ ...prev, compareScenarioId: fallback }));
      }
      return;
    }

    const invalid =
      preferences.compareScenarioId === activeScenarioId ||
      !scenarios.some((scenario) => scenario.id === preferences.compareScenarioId);
    if (invalid) {
      const fallback = scenarios.find((scenario) => scenario.id !== activeScenarioId)?.id || null;
      setPreferences((prev) => ({ ...prev, compareScenarioId: fallback }));
    }
  }, [scenarios, activeScenarioId, preferences.compareScenarioId]);

  const activeRun = activeScenario?.lastRun || null;
  const baseRows = activeRun?.rows || [];
  const filteredRows = useMemo(() => filterSimulationRows(baseRows, filters), [baseRows, filters]);

  const currentAggregate = useMemo(
    () => aggregateRows(filteredRows, activeScenario?.parameters?.tolerancePercent || 0),
    [filteredRows, activeScenario]
  );

  const resultRows = useMemo(
    () =>
      buildResultRows(filteredRows, {
        groupBy: layout.groupBy,
        tolerancePercent: activeScenario?.parameters?.tolerancePercent || 0,
      }),
    [filteredRows, layout.groupBy, activeScenario]
  );

  const filterOptions = useMemo(() => {
    if (baseRows.length) return buildUniqueOptions(baseRows);
    return buildUniqueOptions(
      machines.map((machine) => ({
        site: machine.site,
        line: machine.line,
        riskLevel: machine.riskLevel,
      }))
    );
  }, [baseRows, machines]);

  const scenarioSummaries = useMemo(
    () =>
      scenarios.map((scenario) => {
        const rows = Array.isArray(scenario?.lastRun?.rows) ? scenario.lastRun.rows : [];
        const aggregate = aggregateRows(rows, scenario?.parameters?.tolerancePercent || 0);
        return {
          id: scenario.id,
          name: scenario.name,
          kind: scenario.kind,
          period: scenario.period,
          isDirty: !!scenario.isDirty,
          aggregate,
          lastRunAt: scenario.lastRun?.ranAt || null,
        };
      }),
    [scenarios]
  );

  const compareScenario = useMemo(
    () =>
      scenarios.find((scenario) => scenario.id === preferences.compareScenarioId) || null,
    [scenarios, preferences.compareScenarioId]
  );

  const comparison = useMemo(() => {
    const baseScenarioRows = activeScenario?.lastRun?.rows || [];
    const targetScenarioRows = compareScenario?.lastRun?.rows || [];
    const baseFiltered = filterSimulationRows(baseScenarioRows, filters);
    const targetFiltered = filterSimulationRows(targetScenarioRows, filters);
    return {
      base: {
        scenarioId: activeScenario?.id || null,
        name: activeScenario?.name || "-",
        period: activeScenario?.period || "-",
        aggregate: aggregateRows(baseFiltered, activeScenario?.parameters?.tolerancePercent || 0),
      },
      target: {
        scenarioId: compareScenario?.id || null,
        name: compareScenario?.name || "Selecione um cenário",
        period: compareScenario?.period || "-",
        aggregate: aggregateRows(targetFiltered, compareScenario?.parameters?.tolerancePercent || 0),
      },
    };
  }, [activeScenario, compareScenario, filters]);

  const monthlyMachineCapacity = useMemo(() => {
    const monthKeysSet = new Set();
    const collectMonths = (entries) => {
      (entries || []).forEach((entry) => {
        expandDateRange(entry.startDate, entry.endDate).forEach((date) => {
          const key = monthKeyFromIso(date);
          if (key) monthKeysSet.add(key);
        });
      });
    };

    collectMonths(contractHoursCalendar);
    collectMonths(maintenanceWindows);
    collectMonths(teamAbsences);
    collectMonths(teamBoosts);

    if (!monthKeysSet.size) {
      const fallbackYear = 2026;
      for (let month = 1; month <= 12; month += 1) {
        monthKeysSet.add(`${fallbackYear}-${String(month).padStart(2, "0")}`);
      }
    }

    const monthKeys = Array.from(monthKeysSet).sort();
    const byMonth = {};

    monthKeys.forEach((monthKey) => {
      const dates = buildMonthDateList(monthKey);
      const machineRows = (machines || []).map((machine) => {
        const avgPerHour = Number(machine.avgPerHour) || 0;
        const utilizationPct = Number(machine.utilizationRate) || 0;
        let daysWithOperator = 0;
        let operatorEquivalentDays = 0;
        let daysWithoutMaintenance = 0;
        let contractHoursTotal = 0;
        let grossCapacity = 0;

        dates.forEach((date) => {
          const key = `${machine.id}::${date}`;
          const noWork = !!calendarMaps?.noWorkByMachineDate?.[key];
          const extraTeams = Number(calendarMaps?.boostByMachineDate?.[key]) || 0;
          const operatorMultiplier = noWork ? 0 : 1 + extraTeams;
          const hasMaintenance = !!calendarMaps?.maintenanceByMachineDate?.[key];
          const contractHours = Number(calendarMaps?.contractHoursByMachineDate?.[key]) || 0;

          if (operatorMultiplier > 0) daysWithOperator += 1;
          operatorEquivalentDays += operatorMultiplier;
          if (!hasMaintenance) daysWithoutMaintenance += 1;
          contractHoursTotal += contractHours;

          if (!hasMaintenance && operatorMultiplier > 0 && contractHours > 0) {
            grossCapacity += contractHours * avgPerHour * operatorMultiplier;
          }
        });

        const utilizationFactor = Math.max(0, Math.min(1, utilizationPct / 100));
        const marginFactor = 1 - utilizationFactor;
        const safetyMargin = grossCapacity * marginFactor;
        const monthlyCapacity = grossCapacity * utilizationFactor;

        return {
          machineId: machine.id,
          machineCode: machine.code,
          machinePrefix: machine.prefix,
          machineNumber: machine.number,
          productivityPerHour: avgPerHour,
          utilizationPct,
          daysInMonth: dates.length,
          daysWithOperator,
          operatorEquivalentDays,
          daysWithoutMaintenance,
          contractHoursTotal,
          grossCapacity,
          safetyMargin,
          monthlyCapacity,
        };
      });

      const totals = machineRows.reduce(
        (acc, row) => ({
          contractHoursTotal: acc.contractHoursTotal + row.contractHoursTotal,
          grossCapacity: acc.grossCapacity + row.grossCapacity,
          safetyMargin: acc.safetyMargin + row.safetyMargin,
          monthlyCapacity: acc.monthlyCapacity + row.monthlyCapacity,
        }),
        { contractHoursTotal: 0, grossCapacity: 0, safetyMargin: 0, monthlyCapacity: 0 }
      );

      byMonth[monthKey] = {
        monthKey,
        machineRows: machineRows.sort((a, b) => String(a.machinePrefix).localeCompare(String(b.machinePrefix))),
        totals,
      };
    });

    return {
      availableMonths: monthKeys,
      byMonth,
    };
  }, [
    machines,
    contractHoursCalendar,
    maintenanceWindows,
    teamAbsences,
    teamBoosts,
    calendarMaps,
  ]);

  function runActiveScenarioSimulation() {
    if (!activeScenario) return null;
    const runSnapshot = simulationEngineMock.runScenario({
      scenario: activeScenario,
      machines,
      planRows: effectivePlanRows,
      realizedRows: effectiveRealizedRows,
      operatorsSummary,
      maintenanceSchedule: maintenanceWindows,
      periodContext,
    });
    saveScenarioRun(activeScenario.id, runSnapshot);
    setExecutionHistory((prev) => [
      buildHistoryFromRun(activeScenario, runSnapshot),
      ...prev,
    ].slice(0, 30));
    return runSnapshot;
  }

  function updateActiveParameters(patch) {
    if (!activeScenario) return;
    updateScenarioParameters(activeScenario.id, patch);
  }

  function setActivePeriod(period) {
    if (!activeScenario) return;
    updateScenarioPeriod(activeScenario.id, period);
  }

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function resetFilters() {
    setFilters(cloneDefaultFilters());
  }

  function setGroupBy(groupBy) {
    setLayout((prev) => ({ ...prev, groupBy }));
  }

  function toggleVisibleColumn(columnKey) {
    setLayout((prev) => {
      const exists = prev.visibleColumns.includes(columnKey);
      const visibleColumns = exists
        ? prev.visibleColumns.filter((key) => key !== columnKey)
        : [...prev.visibleColumns, columnKey];

      return {
        ...prev,
        visibleColumns:
          visibleColumns.length > 0
            ? visibleColumns
            : [...cloneDefaultLayout().visibleColumns],
      };
    });
  }

  function applyViewById(viewId) {
    const view = views.find((item) => item.id === viewId);
    if (!view) return false;
    const cfg = cloneViewConfig(view.config);
    setFilters((prev) => ({ ...prev, ...cfg.filters }));
    setLayout({
      groupBy: cfg.groupBy,
      visibleColumns: cfg.visibleColumns,
    });
    setActiveViewId(view.id);
    return true;
  }

  function saveCurrentAsView(name) {
    return createView(name, {
      filters,
      groupBy: layout.groupBy,
      visibleColumns: layout.visibleColumns,
    });
  }

  function updateCurrentView() {
    if (!activeViewId) return null;
    return updateView(activeViewId, {
      filters,
      groupBy: layout.groupBy,
      visibleColumns: layout.visibleColumns,
    });
  }

  function deleteViewById(viewId) {
    return deleteView(viewId);
  }

  function setCompareScenarioId(compareScenarioId) {
    setPreferences((prev) => ({ ...prev, compareScenarioId }));
  }

  const visibleColumns = layout.visibleColumns.filter((key) =>
    RESULT_COLUMNS.some((col) => col.key === key)
  );

  return {
    dataCatalog: {
      machines,
      teams,
      operators: { summary: operatorsSummary, roster: teams, pools: [] },
      maintenanceSchedule: maintenanceWindows,
      contractHoursCalendar,
      teamAbsences,
      teamBoosts,
      periods,
      resultColumns: RESULT_COLUMNS,
      filterOptions,
    },

    scenarios: {
      list: scenarios,
      summaries: scenarioSummaries,
      active: activeScenario,
      activeId: activeScenarioId,
      select: setActiveScenarioId,
      create: createScenario,
      duplicate: duplicateScenario,
      rename: renameScenario,
      remove: deleteScenario,
      setKind: setScenarioKind,
      updateParameters: updateActiveParameters,
      setPeriod: setActivePeriod,
      run: runActiveScenarioSimulation,
    },

    views: {
      list: views,
      active: activeView,
      activeId: activeViewId,
      apply: applyViewById,
      saveAs: saveCurrentAsView,
      updateCurrent: updateCurrentView,
      remove: deleteViewById,
    },

    ui: {
      filters,
      layout: { ...layout, visibleColumns },
      updateFilter,
      resetFilters,
      setGroupBy,
      toggleVisibleColumn,
      compareScenarioId: preferences.compareScenarioId,
      setCompareScenarioId,
    },

    results: {
      lastRun: activeRun,
      isOutdated: !!activeScenario?.isDirty,
      filteredRows,
      tableRows: resultRows,
      aggregate: currentAggregate,
      comparison,
      executionHistory,
    },

    bases: {
      machines,
      teams,
      maintenanceWindows,
      contractHoursCalendar,
      teamAbsences,
      teamBoosts,
      operatorsSummary,
      calendarMaps,
      monthlyMachineCapacity,
      actions: baseActions,
    },
  };
}
